import "./LabAttachmentsAddComponent.scss";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import InputComponent from "../../../../shared/components/core/input/InputComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import LoaderComponent from "../../../../shared/components/loader/loaderComponent";
import StatusCardComponent from "../../../../shared/components/status-card/statusCardComponent";
import {CommonService} from "../../../../shared/services";
import AttachmentViewDownloadComponent
    from "../../../../shared/components/core/attachment-view-download/attachmentViewDownloadComponent";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";
import DialogComponent from "../../../../shared/components/dialog/dialogComponent";
import FilePreviewerComponent from "../../../../shared/components/file-previewer/filePreviewerComponent";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import _ from "lodash";
import {ImageConfig} from "../../../../constants";
import PdfPreviewComponent from "../../../../shared/components/pdf-preview/pdfPreviewComponent";
import ErrorComponent from "../../../../shared/components/form-controls/error/errorComponent";
import {useSearchParams} from "react-router-dom";
import useAsyncState from "../../../../shared/hooks/useAsyncState";

interface LabAttachmentsAddComponentProps {
    labID: any;
    labDetails: any;
    cancel: Function;
    next: Function;
    prev: Function;
    updateLabStatus?: () => Promise<void>;
}

const LabAttachmentsAddComponent = (props: LabAttachmentsAddComponentProps) => {
    const {labID, cancel, next, prev, updateLabStatus} = props;
    const labAttachmentFileRef = useRef<HTMLInputElement | null>(null);
    const [labAttachmentList, setLabAttachmentList] = useState<any[]>([]);
    const [isAttachmentListLoading, setIsAttachmentListLoading] = useState<boolean>(false);
    const [isAttachmentListLoaded, setIsAttachmentListLoaded] = useState<boolean>(false);
    const [isAttachmentListLoadingFailed, setIsAttachmentListLoadingFailed] = useState<boolean>(false);
    const [isEdited, setIsEdited] = useState<boolean>(false);

    const [labAttachmentFile, setLabAttachmentFile] = useState<any>(undefined);
    const [labAttachmentTitle, setLabAttachmentTitle] = useState<string>("");
    const [isContinueVisible, setIsContinueVisible] = useState<boolean>(false);
    const [isLabAttachmentBeingUploaded, setIsLabAttachmentBeingUploaded] = useState<boolean>(false);
    const [showLabAttachmentPreviewDialog, setShowLabAttachmentPreviewDialog] = useState<boolean>(false);
    const [isNewLabAttachment, setNewLabAttachment] = useState<boolean>(false)
    const [searchParams] = useSearchParams();
    const lastCompletedStep = parseInt(searchParams.get("lastCompletedStep") || '0');
    const {loading: isLabActivating, execute} = useAsyncState<[], any>();
    const getAttachmentList = useCallback(() => {
        const payload = {};
        setIsAttachmentListLoading(true);
        setIsAttachmentListLoaded(false);
        setIsAttachmentListLoadingFailed(false);
        CommonService._lab.getLabAttachments(labID, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response.data) {
                    setLabAttachmentList(response.data);
                }
                setIsAttachmentListLoading(false);
                setIsAttachmentListLoaded(true);
                setIsAttachmentListLoadingFailed(false);
            })
            .catch((error: any) => {
                setIsAttachmentListLoading(false);
                setIsAttachmentListLoaded(false);
                setIsAttachmentListLoadingFailed(true);
            });
    }, [labID]);

    const handleLabAttachmentSuccess = useCallback((response: any) => {
        setLabAttachmentList((oldState: any) => {
            if (oldState?.length > 0) {
                return [response.data, ...oldState]
            } else {
                return [response.data]
            }
        });
        setIsLabAttachmentBeingUploaded(false);
        setLabAttachmentTitle("");
        setLabAttachmentFile(undefined);
        setShowLabAttachmentPreviewDialog(false);
    }, []);

    const onLabAttachmentSubmit = useCallback(() => {
        const formData = new FormData();
        if (labAttachmentFile) {
            formData.append('image', labAttachmentFile);
        }
        formData.append('attachmentName', labAttachmentTitle);
        formData.append('lastCompletedStep', lastCompletedStep.toString());
        setIsLabAttachmentBeingUploaded(true);
        CommonService._lab.addLabAttachment(labID, formData)
            .then((response: IAPIResponseType<any>) => {
                handleLabAttachmentSuccess(response);
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error uploading lab attachment', "error");
                setIsLabAttachmentBeingUploaded(false);
            })
    }, [labAttachmentFile, handleLabAttachmentSuccess, labAttachmentTitle, labID, lastCompletedStep]);

    const handleLabAttachmentUpload = useCallback((event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = '';
            return;
        }
        event.target.value = null;
        event.target.files = null;
        setIsContinueVisible(true);
        setShowLabAttachmentPreviewDialog(true);
        setLabAttachmentFile(file);
        setIsEdited(true);
        setNewLabAttachment(true);
    }, []);

    const removeLabAttachment = useCallback((attachmentId: any) => {
        const payload = {};
        CommonService._lab.deleteLabAttachment(labID, attachmentId, payload)
            .then((response: IAPIResponseType<any>) => {
                setIsEdited(true);
                setLabAttachmentList((oldState: any) => {
                    const updatedList = _.cloneDeep(oldState);
                    const index = updatedList.findIndex((item: any) => item.id === attachmentId);
                    updatedList.splice(index, 1);
                    return updatedList;
                })
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error deleting lab attachment', "error");
            })
    }, [labID]);

    const handleAttachmentRemove = useCallback((id: any, type: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to delete this attachment?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.DeleteImage}`,
            direction: "up",
            yes: {
                text: "Confirm",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                removeLabAttachment(id);
            })
            .catch(() => {
            })
    }, [removeLabAttachment]);

    const handleNext = useCallback(() => {
        if (updateLabStatus) {
            // Call `execute` with the async function and the parameter
            execute(updateLabStatus).then((res) => {
                console.log(res)
                next(); // Call next after the promise resolves
            })
        } else {
            next();
        }
    }, [execute, updateLabStatus, next])

    useEffect(() => {
        if (lastCompletedStep > 3) {
            getAttachmentList();
        } else {
            setLabAttachmentList([]);
            setIsAttachmentListLoaded(true)
        }
    }, [labID, getAttachmentList, lastCompletedStep]);

    return (
        <div className="lab-attachment-screen screen t-form-screen">
            <FormTitleWithNavigationBack
                title={'Lab Attachments'}
                tooltipData={"Before uploading, make sure that all attachments, such as lab agreements and onboarding forms, are appropriately named. Only JPEG, PNG and PDF files with max. size of 5 MB are allowed."}
                handleClick={prev}
            />
            <div className="t-form attachments-with-title-wrapper">
                <div className={'no-form'}>
                    <div className="t-form-controls-wrapper">
                        <div className="attachment-input-wrapper">
                            <div className={'attachment-input-section'}>
                                <InputComponent
                                    type={"text"}
                                    name={"title"}
                                    label={"Attachment Name"}
                                    placeholder={"Enter Attachment Name"}
                                    value={labAttachmentTitle}
                                    onChange={(value: any) => {
                                        setLabAttachmentTitle(value.replace(/\s/g, ''));
                                    }}
                                />
                                {
                                    labAttachmentTitle.length > 100 &&
                                    <ErrorComponent errorText={'Attachment name should be less than 100 characters'}/>
                                }
                            </div>
                            <input type={"file"}
                                   className={"display-none"}
                                   ref={labAttachmentFileRef}
                                   accept={"image/jpeg,image/gif,image/png,application/pdf,image/x-eps"}
                                   onChange={(event) => {
                                       handleLabAttachmentUpload(event);
                                   }}
                            />
                            <ButtonComponent
                                className="mrg-left-20"
                                disabled={labAttachmentTitle?.length === 0 || labAttachmentTitle?.length > 100}
                                color={"secondary"}
                                variant={"outlined"}
                                fullWidth={false}
                                size={'large'}
                                isLoading={isLabAttachmentBeingUploaded}
                                handleClick={() => {
                                    labAttachmentFileRef.current?.click()
                                }}>
                                Choose
                            </ButtonComponent>
                        </div>

                        <div className="attachment-list-section">
                            {isAttachmentListLoading &&
                                <LoaderComponent/>
                            }
                            {isAttachmentListLoadingFailed &&
                                <StatusCardComponent title={'failed to load attachment list'}/>
                            }
                            {isAttachmentListLoaded && <>
                                {labAttachmentList?.length > 0 &&
                                    <div className="attachment-list-wrapper">
                                        {labAttachmentList?.map((attachment: any) =>
                                            <div key={CommonService.getUUID()}>
                                                <AttachmentViewDownloadComponent
                                                    attachmentTitle={attachment.attachmentName}
                                                    attachment={attachment}
                                                    showDeleteOption={true}
                                                    onClick={(id: any) => handleAttachmentRemove(id, 'lab')}
                                                />
                                            </div>
                                        )}
                                    </div>
                                }
                                {labAttachmentList?.length === 0 &&
                                    <FeedbackComponent className={"mrg-top-25 width-50"}>No attachment have been
                                        uploaded.</FeedbackComponent>
                                }
                            </>
                            }

                        </div>
                    </div>

                    <div className='form-action-section'>
                        <ButtonComponent
                            type="button"
                            color={"primary"}
                            variant={"outlined"}
                            disabled={isLabAttachmentBeingUploaded}
                            fullWidth={true}
                            handleClick={cancel}
                        >
                            Close
                        </ButtonComponent>
                        <ButtonComponent
                            type="button"
                            color={"primary"}
                            variant={"contained"}
                            fullWidth={true}
                            disabled={isLabAttachmentBeingUploaded}
                            isLoading={updateLabStatus && isLabActivating}
                            handleClick={handleNext}
                        >
                            {updateLabStatus ? "Submit" : isEdited ? "Next" : "Skip"}
                        </ButtonComponent>
                    </div>
                </div>
            </div>

            <DialogComponent open={showLabAttachmentPreviewDialog}
                             maxWidth={'md'}
                             title={labAttachmentTitle}
                             isShowCancel={true}
                             onClose={() => {
                                 setShowLabAttachmentPreviewDialog(false)
                                 setLabAttachmentTitle("");
                             }}
                             className={"attachment-preview-dialog"}>
                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    {labAttachmentFile && labAttachmentFile?.type === 'application/pdf' ?
                        <PdfPreviewComponent
                            attachmentFile={labAttachmentFile}
                            isFile={isNewLabAttachment}
                        />
                        :
                        <FilePreviewerComponent
                            attachmentFile={labAttachmentFile}
                            isFile={isNewLabAttachment}/>
                    }
                </div>
                {labAttachmentFile && <div className="uploaded-attachment-file-name">
                    <span className="font-weight-bold"> Uploaded File Name: &nbsp;</span>
                    <span>{labAttachmentFile?.name}</span>
                </div>
                }
                <div className="attachment-preview-dialog-footer">
                    <ButtonComponent variant={"outlined"}
                                     id="cancel_btn"
                                     fullWidth={true}
                                     color={'primary'}
                                     disabled={isLabAttachmentBeingUploaded}
                                     handleClick={() => {
                                         labAttachmentFileRef.current?.click()
                                     }}
                    >
                        Re-upload
                    </ButtonComponent>
                    {
                        isContinueVisible &&
                        <ButtonComponent variant={"contained"}
                                         id="continue_btn"
                                         color={"primary"}
                                         fullWidth={true}
                                         isLoading={isLabAttachmentBeingUploaded}
                                         handleClick={onLabAttachmentSubmit}>
                            Continue
                        </ButtonComponent>
                    }
                </div>
            </DialogComponent>
        </div>
    );

};

export default LabAttachmentsAddComponent;
